import React, { useState, useEffect } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Typography, 
  Divider, 
  Grid,
  Slider,
  Paper,
  Stack,
  Chip,
  Button,
  Menu
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import DateRangeIcon from '@mui/icons-material/DateRange';

const ParametricControls = ({ 
  title, 
  options, 
  onParamsChange,
  defaultParams = {},
  showDateRange = true,
  showSliders = true
}) => {
  const [params, setParams] = useState({
    sector: defaultParams.sector || 'all',
    dateRange: defaultParams.dateRange || 'last6months',
    granularity: defaultParams.granularity || 'monthly',
    confidenceInterval: defaultParams.confidenceInterval || 90,
    ...defaultParams
  });
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  useEffect(() => {
    // Parametreler değiştiğinde üst bileşeni bilgilendir
    onParamsChange && onParamsChange(params);
  }, [params, onParamsChange]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setParams((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSliderChange = (name) => (event, newValue) => {
    setParams((prev) => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleReset = () => {
    setParams({
      sector: 'all',
      dateRange: 'last6months',
      granularity: 'monthly',
      confidenceInterval: 90,
      ...defaultParams
    });
  };

  const sectors = options?.sectors || [
    { value: 'all', label: 'Tüm Sektörler' },
    { value: 'retail', label: 'Perakende' },
    { value: 'manufacturing', label: 'Üretim' },
    { value: 'finance', label: 'Finans' },
    { value: 'telecom', label: 'Telekomünikasyon' },
    { value: 'healthcare', label: 'Sağlık' }
  ];
  
  const dateRanges = options?.dateRanges || [
    { value: 'last3months', label: 'Son 3 Ay' },
    { value: 'last6months', label: 'Son 6 Ay' },
    { value: 'last1year', label: 'Son 1 Yıl' },
    { value: 'last2years', label: 'Son 2 Yıl' },
    { value: 'custom', label: 'Özel Tarih Aralığı' }
  ];
  
  const granularities = options?.granularities || [
    { value: 'daily', label: 'Günlük' },
    { value: 'weekly', label: 'Haftalık' },
    { value: 'monthly', label: 'Aylık' },
    { value: 'quarterly', label: 'Çeyreklik' },
    { value: 'yearly', label: 'Yıllık' }
  ];

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, position: 'relative' }} className="fade-in">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" component="div" gutterBottom>
            {title || "Parametreler"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Grafikleri filtrelemek için parametreleri seçin
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Chip 
            icon={<FilterAltIcon />} 
            label={`Sektör: ${sectors.find(s => s.value === params.sector)?.label}`} 
            color="primary" 
            variant="outlined" 
          />
          {showDateRange && (
            <Chip 
              icon={<DateRangeIcon />} 
              label={`Tarih: ${dateRanges.find(d => d.value === params.dateRange)?.label}`} 
              color="primary" 
              variant="outlined" 
            />
          )}
          <Button
            id="params-button"
            aria-controls={open ? 'params-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            variant="contained"
            onClick={handleClick}
            startIcon={<TuneIcon />}
            size="small"
          >
            Filtreler
          </Button>
          <Menu
            id="params-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'params-button',
            }}
            PaperProps={{
              sx: { width: 350, p: 2 }
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Parametreleri Ayarla
            </Typography>
            <Divider sx={{ my: 1 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="sector-label">Sektör</InputLabel>
                  <Select
                    labelId="sector-label"
                    id="sector"
                    name="sector"
                    value={params.sector}
                    onChange={handleChange}
                    label="Sektör"
                  >
                    {sectors.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {showDateRange && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="dateRange-label">Tarih Aralığı</InputLabel>
                    <Select
                      labelId="dateRange-label"
                      id="dateRange"
                      name="dateRange"
                      value={params.dateRange}
                      onChange={handleChange}
                      label="Tarih Aralığı"
                    >
                      {dateRanges.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="granularity-label">Granülasyon</InputLabel>
                  <Select
                    labelId="granularity-label"
                    id="granularity"
                    name="granularity"
                    value={params.granularity}
                    onChange={handleChange}
                    label="Granülasyon"
                  >
                    {granularities.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {showSliders && (
                <Grid item xs={12}>
                  <Typography id="confidence-interval-slider" gutterBottom>
                    Güven Aralığı: {params.confidenceInterval}%
                  </Typography>
                  <Slider
                    size="small"
                    aria-labelledby="confidence-interval-slider"
                    value={params.confidenceInterval}
                    onChange={handleSliderChange('confidenceInterval')}
                    min={50}
                    max={99}
                    step={1}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReset}
                  startIcon={<RefreshIcon />}
                  sx={{ mr: 1 }}
                >
                  Sıfırla
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleClose}
                >
                  Uygula
                </Button>
              </Grid>
            </Grid>
          </Menu>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ParametricControls;
